import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {formatLocalDateTime} from '@common/utils/dates'
import {DateTime} from 'luxon'

import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {Icon} from '~/components/general/icon/Icon'
import {SimpleTable} from '~/components/general/table/SimpleTable'
import {TableCellIconButtonPopup} from '~/components/patients/common/patientsTable/PatientsTable.styled'
import {theme} from '~/styles/theme'
import {useGetPartnerAdminPractitionersTimeOff} from '~/api/partnerAdminPractitionersTimeOff'
import {useDeletePractitionerTimeOff} from '~/api/practitionerTimeOff'
import {StaffTimeOffModal} from '~/components/partnerAdmin/staff/StaffTimeOffModal'
import {useGetPartnerAdministratorPartner} from '~/api/partnerAdministrator'

type RowActionMenuProps = {
  id: string
  practitionerId: string
  onEdit: () => void
}

const RowActionMenu: React.FC<RowActionMenuProps> = ({
  id,
  practitionerId,
  onEdit,
}) => {
  const {t} = useTranslation<string>()
  const {mutate: deleteTimeOff} = useDeletePractitionerTimeOff({
    id,
    practitionerId,
  })

  const menuItems = [
    {
      label: t('Edit'),
      onClick: onEdit,
    },
    {
      label: t('Delete'),
      onClick: () => {
        if (window.confirm(t('Are you sure you want to delete this item?'))) {
          deleteTimeOff(undefined)
        }
      },
    },
  ]

  return <TableCellIconButtonPopup menuItems={menuItems} />
}

export const TimeOffPage: React.FC = () => {
  const {t} = useTranslation<string>()
  const {data, isLoading} = useGetPartnerAdminPractitionersTimeOff()
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const {data: partner} = useGetPartnerAdministratorPartner()
  const partnerTimeZone = partner?.timeZone

  const editingItem = useMemo(
    () => data?.find((item) => item.id === editingId) || null,
    [data, editingId],
  )

  const headerData = useMemo(
    () => ({
      columns: [
        {title: t('Staff'), columnWidthPercent: 30},
        {title: t('Start time'), columnWidthPercent: 30},
        {title: t('End time'), columnWidthPercent: 30},
        {title: '', columnWidthPercent: 10},
      ],
    }),
    [t],
  )

  const rows = useMemo(
    () =>
      (data || []).map((item) => ({
        columns: [
          `${item.practitionerFirstName} ${item.practitionerSecondName}`.trim(),
          partnerTimeZone
            ? DateTime.fromISO(item.startTime)
                .setZone(partnerTimeZone)
                .toFormat('yyyy-LL-dd HH:mm')
            : formatLocalDateTime(item.startTime),
          partnerTimeZone
            ? DateTime.fromISO(item.endTime)
                .setZone(partnerTimeZone)
                .toFormat('yyyy-LL-dd HH:mm')
            : formatLocalDateTime(item.endTime),
        ],
        actionComponent: (
          <RowActionMenu
            id={item.id}
            practitionerId={item.practitionerId}
            onEdit={() => {
              setEditingId(item.id)
              setShowModal(true)
            }}
          />
        ),
      })),
    [data, partnerTimeZone],
  )

  return (
    <Container flex direction="column" spacing={4}>
      <Container flex justify="space-between" align="center">
        <Typography size="lg" weight="medium">
          {t('Time off')}
        </Typography>
        <Button
          size="sm"
          startIcon={<Icon name="Plus" color={theme.colors.white} size="sm" />}
          onClick={() => {
            setEditingId(null)
            setShowModal(true)
          }}>
          {t('Add time off')}
        </Button>
      </Container>

      <SimpleTable header={headerData} rows={rows} isLoading={isLoading} />

      <StaffTimeOffModal
        isVisible={showModal}
        onClose={() => setShowModal(false)}
        timeOffSummary={editingItem}
      />
    </Container>
  )
}
